// glonet-react-tailwind-project--src--components--HostsLanding.js
// Public marketing page for prospective nexus HOSTS and AFFILIATES, served
// full-bleed at /hosts as a native sibling of GlonetAiLanding. "Request host
// access" requires being signed in (hosts must be members): signed-out
// visitors round-trip through /signin and come back with ?request=1, which
// reopens the request window automatically.
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import HostRequestDialog from "./HostRequestDialog";
import { SPLIT_FALLBACK, computeSplit } from "./hostSplit";

const ACCENT = "#74a0f5";
const ACCENT_STRONG = "#5b8cf0";
const GLOW = "rgba(116,160,245,0.22)";

const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

export default function HostsLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestSent, setRequestSent] = useState(false);

  // Same host logic as GlonetAiLanding: the landing lives at "/" on the
  // glonet.ai domain and at /glonet-ai everywhere else.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const onGlonetAiHost = host === "glonet.ai" || host === "www.glonet.ai";
  const landingPath = onGlonetAiHost ? "/" : "/glonet-ai";

  const goLanding = (e) => {
    e.preventDefault();
    navigate(landingPath);
  };

  const requestHostAccess = async (e) => {
    if (e) e.preventDefault();
    try {
      await Auth.currentAuthenticatedUser();
      setRequestOpen(true);
    } catch (err) {
      // hosts must be members first — sign in (or create an account), then
      // land back here with the request window already open
      navigate("/signin", { state: { from: "/hosts?request=1" } });
    }
  };

  // Post-sign-in return leg: ?request=1 reopens the window, then cleans the URL.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("request") !== "1") return;
    Auth.currentAuthenticatedUser()
      .then(() => setRequestOpen(true))
      .catch(() => {})
      .finally(() => navigate("/hosts", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // /hosts#calculator (the story's landing point) scrolls to the split
  // calculator once the page has painted.
  useEffect(() => {
    if (location.hash !== "#calculator") return;
    const t = setTimeout(() => {
      const el = document.getElementById("calculator");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [location.hash]);

  // One public-activity fetch feeds everything on this page: the honest
  // pitch copy switch, the silent-mic threshold, and the split calculator's
  // real economics. The plan config lives HERE so the request dialog and the
  // voice agent see exactly what the calculator shows.
  const [convOn, setConvOn] = useState(false);
  const [micSecs, setMicSecs] = useState(5);
  const [splitInputs, setSplitInputs] = useState(SPLIT_FALLBACK);
  const [planCfg, setPlanCfg] = useState({ model: "subscription", price: 20, members: 500, wc: true, convos: false });
  // Whether the visitor has actually engaged with the plan — via the page
  // calculator or the dialog's own editor. Until then the plan is just
  // defaults, and the intake agent must build the plan WITH them instead of
  // "confirming" numbers they never chose.
  const [planTouched, setPlanTouched] = useState(false);
  const touchPlanCfg = (updater) => { setPlanTouched(true); setPlanCfg(updater); };
  useEffect(() => {
    import("../services/api").then(({ default: api }) =>
      api.get("public/watercooler/activity")
        .then(({ data }) => {
          setConvOn(!!data.conversations_enabled);
          if (data.mic_silence_secs) setMicSecs(data.mic_silence_secs);
          if (data.split_inputs) setSplitInputs((f) => ({ ...f, ...data.split_inputs }));
        })
        .catch(() => {}));
  }, []);

  return (
    <div
      style={{
        background: "#0c0d10",
        color: "#f1f2f4",
        minHeight: "100vh",
        fontFamily: "'Hanken Grotesk',system-ui,sans-serif",
        WebkitFontSmoothing: "antialiased",
        overflowX: "hidden",
        position: "relative",
      }}
    >
      <style>{`
        @keyframes hl-breathe { 0%,100% { opacity: 0.55; transform: translateX(-50%) scale(1); } 50% { opacity: 0.8; transform: translateX(-50%) scale(1.06); } }
        .hl-link { color:#b6b9c0; text-decoration:none; transition:color .15s; }
        .hl-link:hover { color:#fff; }
        .hl-btn { transition:background .15s, border-color .15s; }
        .hl-btn-primary:hover { background:${ACCENT_STRONG} !important; }
        .hl-btn-ghost:hover { border-color:${ACCENT} !important; }
        .hl-foot { color:#7c818b; text-decoration:none; transition:color .15s; }
        .hl-foot:hover { color:#f1f2f4; }
        @media (max-width: 840px) {
          .hl-grid-2, .hl-grid-3, .hl-grid-4 { grid-template-columns:1fr !important; gap:28px !important; }
        }
      `}</style>

      {/* ambient hero glow */}
      <div
        style={{
          position: "absolute",
          top: "-220px",
          left: "50%",
          transform: "translateX(-50%)",
          width: "1000px",
          height: "680px",
          background: `radial-gradient(circle at center, ${GLOW} 0%, transparent 68%)`,
          filter: "blur(14px)",
          pointerEvents: "none",
          zIndex: 0,
          animation: "hl-breathe 9s ease-in-out infinite",
        }}
      />

      {/* NAV — minimal: wordmark back to the landing, one CTA */}
      <nav
        style={{
          position: "relative",
          zIndex: 3,
          maxWidth: "1140px",
          margin: "0 auto",
          padding: "26px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
        }}
      >
        <a
          href={landingPath}
          onClick={goLanding}
          style={{ display: "flex", alignItems: "baseline", gap: "12px", textDecoration: "none", color: "inherit" }}
        >
          <span style={{ fontFamily: SERIF, fontSize: "25px", letterSpacing: "-0.01em" }}>
            glonet<span style={{ color: ACCENT }}>.ai</span>
          </span>
          <span style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>
            Hosts&nbsp;&amp;&nbsp;Affiliates
          </span>
        </a>
        <button
          onClick={requestHostAccess}
          className="hl-btn hl-btn-primary"
          style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", cursor: "pointer", padding: "10px 18px", borderRadius: "999px", whiteSpace: "nowrap" }}
        >
          Request host access
        </button>
      </nav>

      {/* HERO */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "88px 32px 96px", textAlign: "center" }}>
        <div style={{ fontFamily: MONO, fontSize: "16px", letterSpacing: "0.26em", color: ACCENT, textTransform: "uppercase", marginBottom: "30px" }}>
          Run your own nexus
        </div>
        <h1 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(42px,6.4vw,82px)", lineHeight: 1.02, letterSpacing: "-0.015em", margin: "0 auto", maxWidth: "15ch", textWrap: "balance" }}>
          You built your audience. Now give it a place to meet.
        </h1>
        <p style={{ fontSize: "19px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "62ch", margin: "30px auto 0" }}>
          A nexus is an always-on community on the Glonet platform: AI matchmaking
          across your members, voice-first onboarding instead of forms, a Water
          Cooler venue where members mingle as avatars{convOn ? " — and agents that meet before the humans do" : ""}.
          Your people stop scrolling past each other and start finding each other.
        </p>
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", marginTop: "40px" }}>
          <button
            onClick={requestHostAccess}
            className="hl-btn hl-btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", cursor: "pointer", padding: "15px 24px", borderRadius: "999px" }}
          >
            Request host access
          </button>
          <button
            onClick={() => navigate("/hosts/story")}
            className="hl-btn hl-btn-ghost"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 600, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", padding: "15px 24px", borderRadius: "999px" }}
          >
            Watch the story
          </button>
        </div>
        {/* The way in for somebody who already hosts. Without this the host
            dashboard is reachable only from the weekly email, which is exactly
            the "you have to remember it exists" problem the whole AI Ops pass
            is trying to remove. Shown to everyone: the page itself says
            "you're not a host yet" to anyone who is not. */}
        <p style={{ marginTop: "22px", fontSize: "14px", color: "#7c818b" }}>
          Already hosting?{" "}
          <a href="/hosts/dashboard" style={{ color: ACCENT }}
             onClick={(e) => { e.preventDefault(); navigate("/hosts/dashboard"); }}>
            Your members and your money →
          </a>
        </p>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* TWO PATHS */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>Two ways in</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "22ch" }}>
          Send people, or build the room they walk into.
        </h2>
        <div className="hl-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "30px", marginTop: "56px" }}>
          {/* THE AFFILIATE */}
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "34px 36px" }}>
            <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.22em", color: ACCENT, textTransform: "uppercase", marginBottom: "16px" }}>
              The Affiliate
            </div>
            <h3 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "28px", lineHeight: 1.15, margin: "0 0 14px" }}>
              Your network is the product.
            </h3>
            <p style={{ fontSize: "16px", lineHeight: 1.65, color: "#b6b9c0", margin: 0 }}>
              If your LinkedIn feed is full of people asking to be introduced,
              you're already doing the job — just unpaid. Share your affiliate
              link; you earn a share of every membership payment your people
              make, paid out automatically by Stripe.
            </p>
            <p style={{ fontSize: "16px", lineHeight: 1.65, color: "#9a9ea7", margin: "14px 0 0" }}>
              The glonet.ai founding program is open now: 50% of every
              subscription you bring, for 12 months per subscriber, first 100
              affiliates only.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "22px" }}>
              <button onClick={() => navigate("/affiliates")} className="hl-btn hl-btn-primary"
                      style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", cursor: "pointer", padding: "11px 20px", borderRadius: "999px" }}>
                Become an affiliate
              </button>
              <button onClick={() => navigate("/affiliates/story")} className="hl-btn hl-btn-ghost"
                      style={{ fontSize: "14px", fontWeight: 600, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", padding: "11px 20px", borderRadius: "999px" }}>
                Watch the affiliate story
              </button>
            </div>
          </div>
          {/* THE HOST */}
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "34px 36px" }}>
            <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.22em", color: ACCENT, textTransform: "uppercase", marginBottom: "16px" }}>
              The Host
            </div>
            <h3 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "28px", lineHeight: 1.15, margin: "0 0 14px" }}>
              Your community, running as a nexus.
            </h3>
            <p style={{ fontSize: "16px", lineHeight: 1.65, color: "#b6b9c0", margin: 0 }}>
              For the channel, the podcast, the community that already gathers
              around you: a nexus of your own. Your brand and your pricing —
              one-time event passes or recurring memberships. Your own Water
              Cooler venue, on by default. AI matchmaking that works your
              members against each other, not against the whole internet.
            </p>
            <p style={{ fontSize: "16px", lineHeight: 1.65, color: "#9a9ea7", margin: "14px 0 0" }}>
              Every sale pays out to your Stripe account. You set your own
              affiliate terms and invite affiliates to grow it.
            </p>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "22px" }}>
              <button onClick={requestHostAccess} className="hl-btn hl-btn-primary"
                      style={{ fontSize: "14px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", cursor: "pointer", padding: "11px 20px", borderRadius: "999px" }}>
                Request host access
              </button>
              <a href="#calculator" onClick={(e) => { e.preventDefault(); document.getElementById("calculator")?.scrollIntoView({ behavior: "smooth" }); }}
                 className="hl-btn hl-btn-ghost"
                 style={{ display: "inline-flex", alignItems: "center", fontSize: "14px", fontWeight: 600, color: "#f1f2f4", background: "transparent", border: "1px solid rgba(255,255,255,0.25)", cursor: "pointer", padding: "11px 20px", borderRadius: "999px", textDecoration: "none" }}>
                See the split math
              </a>
            </div>
          </div>
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* HOW THE MONEY WORKS */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>How the money works</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "18ch" }}>
          One payment, split three ways.
        </h2>
        <div className="hl-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "30px", marginTop: "56px" }}>
          {[
            ["Platform", "Glonet takes its percentage of each membership payment. That is the entire platform fee — no listing costs, no charge for the venue or the matchmaking."],
            ["Host", "The host keeps the remainder, less whatever affiliate share they've chosen to offer. Hosts set their own affiliate terms, per nexus."],
            ["Affiliate", "Affiliates earn the host-configured share of every payment from members they referred, paid out automatically as Stripe transfers."],
          ].map(([who, body]) => (
            <div key={who} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "18px", padding: "28px 30px" }}>
              <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.22em", color: ACCENT, textTransform: "uppercase", marginBottom: "14px" }}>{who}</div>
              <p style={{ fontSize: "15.5px", lineHeight: 1.6, color: "#b6b9c0", margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      <div style={{ maxWidth: "1140px", margin: "0 auto", height: "1px", background: "rgba(255,255,255,0.07)" }} />

      {/* WHAT YOUR MEMBERS GET */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px" }}>
        <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>What your members get</div>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "20ch" }}>
          The reason they'll pay: it works while they sleep.
        </h2>
        <div className="hl-grid-4" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "30px", marginTop: "56px" }}>
          {[
            ["01 / Speak", "Join by talking", "New members say what they're looking for — out loud. A voice agent writes it up; no forms, no profile homework."],
            ["02 / Match", "Agents meet first",
             convOn
               ? "The matchmaker scores your whole nexus for fit, and members' agents hold a short pre-meeting before any human commits time."
               : "The matchmaker scores your whole nexus for fit."],
            ["03 / Connect", "Email only when it's worth it", "No feed to tend. When there's a real reason to meet, both members get an email with the why and the transcript."],
            ["04 / Mingle", "The Water Cooler", "An always-open venue where your members mingle as avatars — voice and video, public and private rooms they open themselves, no links, no scheduling."],
          ].map(([step, title, body]) => (
            <div key={step}>
              <div style={{ fontFamily: MONO, fontSize: "13px", color: ACCENT, marginBottom: "14px" }}>{step}</div>
              <h3 style={{ fontSize: "19px", fontWeight: 600, margin: "0 0 10px" }}>{title}</h3>
              <p style={{ fontSize: "15px", lineHeight: 1.6, color: "#9a9ea7", margin: 0 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SPLIT CALCULATOR — self-service: hosts derive the platform share
          themselves instead of negotiating it. Indicative only.
          id=calculator is the /hosts/story landing anchor. */}
      <div id="calculator">
        <SplitCalculator cfg={planCfg} setCfg={touchPlanCfg} inputs={splitInputs} />
      </div>

      {/* CTA */}
      <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "120px 32px 110px", textAlign: "center" }}>
        <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(40px,5.6vw,72px)", lineHeight: 1.02, letterSpacing: "-0.015em", margin: 0 }}>
          Bring your people.
        </h2>
        <p style={{ fontSize: "18px", lineHeight: 1.6, color: "#b6b9c0", maxWidth: "48ch", margin: "22px auto 0" }}>
          Host access is granted personally while the platform is young. Tell us
          about your audience and we'll set you up.
        </p>
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap", marginTop: "38px" }}>
          <button
            onClick={requestHostAccess}
            className="hl-btn hl-btn-primary"
            style={{ display: "inline-flex", alignItems: "center", gap: "10px", fontSize: "15px", fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none", cursor: "pointer", padding: "15px 26px", borderRadius: "999px" }}
          >
            Request host access
          </button>
        </div>
        {requestSent && (
          <div style={{ fontSize: "14.5px", color: "#57d9a4", marginTop: "20px" }}>
            ✓ Your host request is on its way — we&apos;ll be in touch by email.
          </div>
        )}
        <div style={{ fontSize: "13.5px", color: "#7c818b", marginTop: "24px" }}>
          Rather send people than host?{" "}
          <a href="/affiliates" onClick={(e) => { e.preventDefault(); navigate("/affiliates"); }}
             style={{ color: ACCENT, textDecoration: "none" }}>
            Become a founding affiliate →
          </a>
        </div>
        <div style={{ fontFamily: MONO, fontSize: "11px", letterSpacing: "0.1em", color: "#5c616b", marginTop: "20px" }}>
          glonet.ai · powered by the Glonet platform
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{ position: "relative", zIndex: 2, borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth: "1140px", margin: "0 auto", padding: "30px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "20px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
            <span style={{ fontFamily: SERIF, fontSize: "20px" }}>glonet<span style={{ color: ACCENT }}>.ai</span></span>
            <span style={{ fontFamily: MONO, fontSize: "10px", letterSpacing: "0.24em", color: "#5c616b", textTransform: "uppercase" }}>Hosts &amp; Affiliates</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "26px", fontSize: "13px", color: "#7c818b" }}>
            <a href={landingPath} onClick={goLanding} className="hl-foot">glonet.ai</a>
            <a href="https://mml-glonet.com" target="_blank" rel="noopener noreferrer" className="hl-foot">The app ↗</a>
            <span>© 2026</span>
          </div>
        </div>
      </footer>

      {requestOpen && (
        <HostRequestDialog
          onClose={() => setRequestOpen(false)}
          onSent={() => setRequestSent(true)}
          cfg={planCfg}
          setCfg={touchPlanCfg}
          inputs={splitInputs}
          planTouched={planTouched}
          micSilenceSecs={micSecs}
        />
      )}
    </div>
  );
}

/* ---- Split calculator UI. The economic model itself lives in hostSplit.js,
   shared with the request dialog's editable plan card and the voice agent's
   set_plan tool — all must describe the SAME plan. */
function SplitCalculator({ cfg, setCfg, inputs }) {
  const { model, price, members, wc, convos } = cfg;
  const setPart = (k) => (v) => setCfg((prev) => ({ ...prev, [k]: v }));
  const [setModel, setPrice, setMembers, setWc, setConvos] =
    [setPart("model"), setPart("price"), setPart("members"), setPart("wc"), setPart("convos")];

  const I = inputs;
  const { p, B, cost, fees, floor, rawPct, viable, platformPct, hostPct, minPrice,
          floorBinds, aboveBase, hostMonthly } = computeSplit(I, cfg);

  const num = (v, set, min, max) => (e) => {
    const n = Number(e.target.value);
    set(Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : min);
  };
  const field = { fontFamily: MONO, fontSize: "13px", color: "#f1f2f4", background: "#0c0d10",
                  border: "1px solid rgba(255,255,255,0.18)", borderRadius: "8px", padding: "8px 10px", width: "110px" };
  const lbl = { fontSize: "13px", color: "#9a9ea7", display: "block", marginBottom: "6px" };
  const toggle = (on) => ({ fontSize: "13px", cursor: "pointer", padding: "8px 14px", borderRadius: "999px",
                            color: on ? "#0c0d10" : "#b6b9c0", background: on ? ACCENT : "transparent",
                            border: on ? "none" : "1px solid rgba(255,255,255,0.18)" });
  const rowStyle = { display: "flex", justifyContent: "space-between", fontSize: "13px",
                     color: "#9a9ea7", padding: "3px 0" };

  return (
    <section style={{ position: "relative", zIndex: 2, maxWidth: "1140px", margin: "0 auto", padding: "104px 32px 0" }}>
      <div style={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.26em", color: "#6b6f78", textTransform: "uppercase" }}>Plan your nexus</div>
      <h2 style={{ fontFamily: SERIF, fontWeight: 400, fontSize: "clamp(30px,3.6vw,46px)", lineHeight: 1.08, letterSpacing: "-0.01em", margin: "18px 0 0", maxWidth: "20ch" }}>
        See the split before you ask.
      </h2>
      <p style={{ fontSize: "15px", lineHeight: 1.6, color: "#9a9ea7", maxWidth: "58ch", margin: "16px 0 0" }}>
        Simple rule: the first ${B} of your price counts as platform value and splits 50/50 with
        us, like our Partner affiliate deal. Above ${B} it is <i>your</i> brand: you keep{" "}
        {Math.round((1 - I.platform_share_above_base) * 100)}% up to ${I.tier2_at || 100},{" "}
        {Math.round((1 - (I.platform_share_tier2 ?? I.platform_share_above_base)) * 100)}% up to
        ${(I.tier3_at || 1000).toLocaleString()}, and{" "}
        {Math.round((1 - (I.platform_share_tier3 ?? I.platform_share_above_base)) * 100)}% beyond —
        stepped like tax brackets, so there are no cliffs. The ${B} baseline tracks
        Glonet&apos;s own membership price — not final yet; the calculator follows it
        automatically.
      </p>
      <div className="hl-grid-2" style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: "40px", marginTop: "40px", alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
          <div>
            <span style={lbl}>Pricing model</span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={toggle(model === "subscription")} onClick={() => setModel("subscription")}>Subscription</button>
              <button style={toggle(model === "one_time")} onClick={() => setModel("one_time")}>One-time tickets</button>
            </div>
          </div>
          <div style={{ display: "flex", gap: "22px", flexWrap: "wrap" }}>
            <div>
              <span style={lbl}>{model === "subscription" ? "Price ($/member/mo)" : "Ticket price ($)"}</span>
              <input type="number" min="1" max="5000" value={price} onChange={num(price, setPrice, 1, 5000)} style={field} />
            </div>
            <div>
              <span style={lbl}>Expected members</span>
              <input type="number" min="10" max="1000000" value={members} onChange={num(members, setMembers, 10, 1000000)} style={field} />
            </div>
          </div>
          <div style={{ fontSize: "13.5px", lineHeight: 1.55, color: "#dfe1e5",
            background: "rgba(116,160,245,0.10)", border: "1px solid rgba(116,160,245,0.35)",
            borderLeft: `3px solid ${ACCENT}`, borderRadius: "10px", padding: "12px 14px" }}>
            <b style={{ color: ACCENT }}>Price is the lever.</b> Change the price and your split
            percentage changes with it. Member count never moves the split — it only multiplies
            what you earn each month.
          </div>
          <div style={{ display: "flex", gap: "22px", flexWrap: "wrap" }}>
            <div>
              <span style={lbl}>Water Cooler venue</span>
              <div style={{ display: "flex", gap: "10px" }}>
                <button style={toggle(wc)} onClick={() => setWc(true)}>Yes</button>
                <button style={toggle(!wc)} onClick={() => setWc(false)}>No</button>
              </div>
            </div>
            <div>
              <span style={lbl}>Agent pre-meetings (audio)</span>
              <div style={{ display: "flex", gap: "10px" }}>
                <button style={toggle(convos)} onClick={() => setConvos(true)}>Yes</button>
                <button style={toggle(!convos)} onClick={() => setConvos(false)}>No</button>
              </div>
            </div>
          </div>
        </div>
        <div style={{ background: "#131519", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "20px", padding: "26px 28px" }}>
          {model === "one_time" ? (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: "16px" }}>
                <div style={{ fontFamily: SERIF, fontSize: "44px" }}>{100 - I.one_time_platform_pct}<span style={{ fontSize: "22px" }}>%</span></div>
                <div style={{ fontSize: "14px", color: "#b6b9c0" }}>yours</div>
                <div style={{ fontFamily: MONO, fontSize: "13px", color: "#7c818b", marginLeft: "auto" }}>platform {I.one_time_platform_pct}%</div>
              </div>
              <div style={{ fontSize: "14px", lineHeight: 1.7, color: "#9a9ea7", marginTop: "14px" }}>
                One-time ticket nexuses run on the standard {I.one_time_platform_pct}% platform share.
                {p < 3 && <span style={{ color: "#e0a35e" }}> Note: on a ${p} ticket, card fees alone take ${fees.toFixed(2)} — price tickets at $3+.</span>}
              </div>
            </>
          ) : viable ? (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: "16px" }}>
                <div style={{ fontFamily: SERIF, fontSize: "44px" }}>{hostPct}<span style={{ fontSize: "22px" }}>%</span></div>
                <div style={{ fontSize: "14px", color: "#b6b9c0" }}>yours</div>
                <div style={{ fontFamily: MONO, fontSize: "13px", color: "#7c818b", marginLeft: "auto" }}>platform {platformPct}%</div>
              </div>
              <div style={{ marginTop: "14px", paddingTop: "10px", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={rowStyle}>
                  <span>Platform value (first ${Math.min(p, B)} — affiliate-style 50/50)</span>
                  <span>${(I.platform_share_of_base * Math.min(p, B)).toFixed(2)}</span>
                </div>
                {aboveBase && (
                  <div style={rowStyle}>
                    <span>Your brand (${(p - B).toFixed(0)} above baseline — you keep {Math.round((1 - I.platform_share_above_base) * 100)}%)</span>
                    <span>${(I.platform_share_above_base * (p - B)).toFixed(2)}</span>
                  </div>
                )}
                {floorBinds && (
                  <div style={rowStyle}>
                    <span style={{ color: "#e0a35e" }}>Cost floor binds: fees ${fees.toFixed(2)} + 2× costs ${cost.toFixed(2)}/member</span>
                    <span style={{ color: "#e0a35e" }}>${floor.toFixed(2)}</span>
                  </div>
                )}
              </div>
              <div style={{ fontSize: "14px", lineHeight: 1.7, color: "#9a9ea7", marginTop: "12px" }}>
                At {members.toLocaleString()} members that&apos;s about{" "}
                <b style={{ color: "#b6b9c0" }}>${(hostMonthly || 0).toLocaleString()}/mo to you</b>.
                {aboveBase && " The premium above the baseline is your audience's trust in you — so it's mostly yours."}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontFamily: SERIF, fontSize: "34px", color: "#e0a35e" }}>This price doesn&apos;t work.</div>
              <div style={{ fontSize: "14px", lineHeight: 1.7, color: "#9a9ea7", marginTop: "12px" }}>
                At <b style={{ color: "#b6b9c0" }}>${p}/mo</b>, platform costs
                (${cost.toFixed(2)}/member{convos ? " incl. audio pre-meetings" : ""}) plus card fees
                (${fees.toFixed(2)}) would need <b style={{ color: "#e0a35e" }}>{Math.ceil(rawPct)}%</b> of
                every subscription — nobody makes money, including you.
              </div>
              <div style={{ fontSize: "15px", lineHeight: 1.7, color: "#b6b9c0", marginTop: "12px" }}>
                Minimum workable price with this feature set: <b style={{ color: "#f1f2f4" }}>${minPrice}/mo</b>
                {convos && <span style={{ color: "#9a9ea7" }}> — or drop audio pre-meetings and re-check.</span>}
              </div>
            </>
          )}
          <div style={{ fontFamily: MONO, fontSize: "10.5px", letterSpacing: "0.08em", color: "#5c616b", marginTop: "16px" }}>
            LIVE PLATFORM COSTS · INDICATIVE — FINAL TERMS ARRIVE WITH YOUR HOST APPROVAL
          </div>
        </div>
      </div>
    </section>
  );
}
